import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TopicListCard from '@/components/feature/TopicListCard';
import EmptyState from '@/components/feature/EmptyState';
import { getCategoryById, getTopicsByCategory, sortTopics, type TopicSort } from '@/lib/community';
import { usePageMeta } from '@/hooks/usePageMeta';
import { cn, formatCount } from '@/lib/format';

const SORTS: { key: TopicSort; label: string; icon: string }[] = [
  { key: 'latest', label: '最新', icon: 'ri-time-line' },
  { key: 'hot', label: '最热', icon: 'ri-fire-line' },
  { key: 'replies', label: '回复最多', icon: 'ri-chat-3-line' },
];

const STEP = 4;

export default function CategoryPage() {
  const { categoryId = '' } = useParams();
  const navigate = useNavigate();
  const category = getCategoryById(categoryId);
  const [sort, setSort] = useState<TopicSort>('latest');
  const [visible, setVisible] = useState(STEP);

  const baseTopics = useMemo(() => (category ? getTopicsByCategory(category.id) : []), [category]);
  const sorted = useMemo(() => sortTopics(baseTopics, sort), [baseTopics, sort]);

  usePageMeta({
    title: category ? `${category.name} - 蜜糖社区` : '分类未找到 - 蜜糖社区',
    description: category
      ? `${category.description} 浏览蜜糖社区${category.name}分类下的热门话题、经验分享与讨论交流。`
      : '该分类不存在，返回蜜糖社区首页继续浏览兴趣话题。',
    keywords: category ? `${category.name},蜜糖社区,${category.name}话题,兴趣交流,经验分享` : '蜜糖社区,话题分类',
  });

  if (!category) {
    return (
      <Layout>
        <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-16">
          <EmptyState
            icon="ri-error-warning-line"
            title="没有找到这个分类"
            description="它可能已经被调整或不存在，回到首页看看其他有趣的讨论区吧。"
            actionLabel="回到首页"
            onAction={() => { navigate('/'); }}
          />
        </div>
      </Layout>
    );
  }

  const shown = sorted.slice(0, visible);
  const hasMore = visible < sorted.length;

  const reset = () => {
    setSort('latest');
    setVisible(STEP);
  };

  return (
    <Layout>
      <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: category.name }]} />

        {/* Category hero */}
        <div className="mt-5 relative overflow-hidden rounded-xl border border-background-200 h-52 md:h-64">
          <img src={category.image} alt={category.name} title={`${category.name}话题讨论区 - 蜜糖社区`} className="absolute inset-0 w-full h-full object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/10" />
          <div className="relative z-10 h-full flex flex-col justify-center p-6 md:p-10">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
                <i className={`${category.icon} text-xl`} />
              </span>
              <h1 className="text-2xl md:text-3xl font-extrabold text-white">{category.name}讨论区</h1>
            </div>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-white/85 leading-relaxed">{category.description}</p>
            <p className="mt-3 text-xs text-primary-200 font-label font-bold">
              共 {formatCount(category.topicCount)} 个话题 · {sorted.length} 条示例内容
            </p>
          </div>
        </div>

        {/* Filter bar */}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 bg-background-50 border border-background-200 rounded-lg p-3">
          <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
            {SORTS.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => { setSort(item.key); setVisible(STEP); }}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-label font-bold transition-colors cursor-pointer whitespace-nowrap',
                  sort === item.key ? 'bg-background-50 text-primary-700' : 'text-secondary-700 hover:text-secondary-900',
                )}
              >
                <i className={item.icon} />
                {item.label}
              </button>
            ))}
          </div>
          <span className="text-sm text-foreground-500 pl-1">
            当前排序：<span className="font-label font-bold text-foreground-800">{SORTS.find((s) => s.key === sort)?.label}</span>
          </span>
        </div>

        {/* List */}
        <div className="mt-5">
          {shown.length > 0 ? (
            <div className="flex flex-col gap-3">
              {shown.map((topic) => (
                <TopicListCard key={topic.id} topic={topic} />
              ))}
            </div>
          ) : (
            <EmptyState
              title="这个分类下暂时还没有话题"
              description="换个排序方式看看，或者去发布一个属于你的新话题吧。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          )}
        </div>

        {hasMore && (
          <div className="mt-6 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={() => setVisible((v) => v + STEP)}
              className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 text-foreground-800 text-sm font-label font-bold px-6 py-2.5 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              加载更多话题
            </button>
            <p className="text-xs text-foreground-400">
              已显示 {shown.length} / {sorted.length} 条
            </p>
          </div>
        )}

        {!hasMore && sorted.length > 0 && (
          <div className="mt-6 flex items-center justify-center gap-3">
            <span className="h-px w-10 bg-background-300" />
            <p className="text-xs text-foreground-400">已经到底啦</p>
            <span className="h-px w-10 bg-background-300" />
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-2">
          <span className="text-sm text-foreground-500 py-1.5">其他分类：</span>
          {['life', 'entertainment', 'culture', 'tech', 'food', 'travel']
            .filter((id) => id !== category.id)
            .map((id) => {
              const other = getCategoryById(id);
              if (!other) return null;
              return (
                <Link
                  key={id}
                  to={`/category/${id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-label font-bold text-foreground-700 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={other.icon} />
                  {other.name}
                </Link>
              );
            })}
        </div>
      </div>
    </Layout>
  );
}